import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SEOHead } from '../components/seo/SEOHead';
import { NightClubSchema, BreadcrumbSchema } from '../components/seo/SchemaMarkup';
import { Breadcrumbs } from '../components/seo/Breadcrumbs';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { MapPin, Clock, ExternalLink, ArrowRight } from 'lucide-react';

export default function ContactPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--text)]">
      <SEOHead
        title="Contact Scratch Unleashed | Jamshedpur Night Club - Reservations & Enquiries"
        description="Contact Scratch Unleashed, Jamshedpur's best nightclub at Bindal Mall, Sonari. Reserve tables, enquire about private events, birthday parties & VIP bookings. Get in touch now!"
        keywords="contact Scratch Unleashed, Scratch Unleashed phone number, Scratch Unleashed Jamshedpur, Bindal Mall nightclub contact, book table Jamshedpur, nightclub enquiry Jamshedpur"
        canonical="https://scratchjsr.com/contact"
      />
      <NightClubSchema />
      <BreadcrumbSchema items={[
        { name: 'Home', url: 'https://scratchjsr.com/' },
        { name: 'Contact', url: 'https://scratchjsr.com/contact' }
      ]} />

      <div className="h-16"></div>

      <div className="max-w-[1120px] mx-auto px-4 sm:px-6 py-12">
        <Breadcrumbs items={[{ name: 'Contact Us', url: null }]} />

        <div className="mb-12">
          <h1 className="font-bebas text-5xl sm:text-6xl lg:text-7xl mb-4">
            CONTACT US
          </h1>
          <p className="text-xl text-[var(--text-dim)] mb-6">
            Reservations, Private Events & General Enquiries
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Left: Contact Details */}
          <div className="space-y-6">
            <Card className="p-8 bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl">
              <h2 className="font-bebas text-3xl mb-6">Visit Us</h2>
              <div className="space-y-5">
                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-[rgba(212,168,67,0.1)] border border-[rgba(212,168,67,0.2)]">
                    <MapPin size={20} className="text-[var(--accent-gold)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1">Address</p>
                    <p className="text-[var(--text-dim)] text-sm">3rd Floor, Bindal Mall</p>
                    <p className="text-[var(--text-dim)] text-sm">Sonari, Jamshedpur</p>
                    <p className="text-[var(--text-dim)] text-sm">Jharkhand — 831011</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-[rgba(212,168,67,0.1)] border border-[rgba(212,168,67,0.2)]">
                    <Clock size={20} className="text-[var(--accent-gold)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1">Opening Hours</p>
                    <p className="text-[var(--text-dim)] text-sm">Friday – Sunday: 6:00 PM onwards</p>
                    <p className="text-[var(--text-dim)] text-sm">Monday – Thursday: Private events only</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-[rgba(212,168,67,0.1)] border border-[rgba(212,168,67,0.2)]">
                    <ExternalLink size={20} className="text-[var(--accent-gold)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1">WhatsApp & Phone</p>
                    <a
                      href="https://wa.me/917870369666?text=Hi!%20I'd%20like%20to%20book%20a%20table%20at%20Scratch%20Unleashed"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--accent-gold)] text-sm hover:underline"
                    >
                      +91 78703 69666
                    </a>
                    <p className="text-xs text-[var(--text-muted)] mt-1">WhatsApp preferred for bookings</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-[rgba(212,168,67,0.1)] border border-[rgba(212,168,67,0.2)]">
                    <ExternalLink size={20} className="text-[var(--accent-gold)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1">Instagram</p>
                    <a
                      href="https://instagram.com/scratchunleashed"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--accent-gold)] text-sm hover:underline"
                    >
                      @scratchunleashed
                    </a>
                    <p className="text-xs text-[var(--text-muted)] mt-1">DM us for bookings & event info</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="p-3 rounded-xl bg-[rgba(212,168,67,0.1)] border border-[rgba(212,168,67,0.2)]">
                    <ExternalLink size={20} className="text-[var(--accent-gold)]" />
                  </div>
                  <div>
                    <p className="font-semibold text-sm mb-1">Facebook</p>
                    <a
                      href="https://facebook.com/scratchunleashed"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[var(--accent-gold)] text-sm hover:underline"
                    >
                      Scratch Unleashed
                    </a>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl">
              <h2 className="font-bebas text-3xl mb-6">Quick Actions</h2>
              <div className="space-y-3">
                <a
                  href="https://wa.me/917870369666?text=Hi!%20I'd%20like%20to%20book%20a%20table%20at%20Scratch%20Unleashed"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-center gap-2 w-full py-2.5 px-4 rounded-xl text-sm font-semibold text-white transition-all hover:brightness-110"
                  style={{ background: '#25D366', boxShadow: '0 2px 12px rgba(37,211,102,0.3)' }}
                  data-testid="contact-whatsapp-button"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="white">
                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                  </svg>
                  Book via WhatsApp
                </a>
                <Button
                  className="w-full btn-crimson-glow rounded-xl"
                  onClick={() => navigate('/checkin')}
                >
                  Reserve a Table
                  <ArrowRight size={16} className="ml-2" />
                </Button>
                <Button
                  variant="outline"
                  className="w-full rounded-xl border-[var(--border-custom)] text-[var(--text)]"
                  onClick={() => navigate('/vip-table-booking-jamshedpur')}
                >
                  VIP Table Booking
                </Button>
                <Button
                  variant="outline"
                  className="w-full rounded-xl border-[var(--border-custom)] text-[var(--text)]"
                  onClick={() => navigate('/private-party-venue-jamshedpur')}
                >
                  Private Event Enquiry
                </Button>
                <Button
                  variant="outline"
                  className="w-full rounded-xl border-[var(--border-custom)] text-[var(--text)]"
                  onClick={() => navigate('/birthday-party-venue-jamshedpur')}
                >
                  Birthday Party Package
                </Button>
              </div>
            </Card>
          </div>

          {/* Right: Map + Info */}
          <div className="space-y-6">
            <Card className="p-0 overflow-hidden bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl">
              <div className="h-96">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3666.826790556041!2d86.19787467526635!3d22.805879979362773!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x39f5e3bc0f055ce3%3A0x94fae8cb5f18ece8!2sScratch%20Unleashed%20Bar%20%26%20Kitchen!5e0!3m2!1sen!2sin!4v1712345678901!5m2!1sen!2sin"
                  width="100%" height="100%" style={{ border: 0 }}
                  allowFullScreen="" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                  title="Scratch Unleashed Location - Bindal Mall, Sonari, Jamshedpur"
                />
              </div>
              <div className="p-4 border-t border-[var(--border-custom)]">
                <p className="text-sm text-[var(--text-dim)]">
                  <strong className="text-[var(--text)]">Scratch Unleashed</strong> — 3rd Floor, Bindal Mall, Sonari, Jamshedpur, Jharkhand 831011
                </p>
                <a
                  href="https://maps.google.com/?q=Scratch+Unleashed+Jamshedpur"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-[var(--accent-gold)] hover:underline mt-1 inline-block"
                >
                  Open in Google Maps →
                </a>
              </div>
            </Card>

            <Card className="p-6 bg-[var(--surface)] border border-[var(--border-custom)] rounded-2xl">
              <h3 className="font-bebas text-xl mb-3">Getting Here</h3>
              <div className="space-y-2 text-sm text-[var(--text-dim)]">
                <p>• Located in <strong>Bindal Mall, Sonari</strong> — accessible from all parts of Jamshedpur</p>
                <p>• Ample parking available at Bindal Mall (B1 and B2 levels)</p>
                <p>• Ride-sharing pickup/drop available directly outside the mall</p>
                <p>• 5 minutes from Bistupur, 10 minutes from Kadma, 15 minutes from Mango</p>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
